import { useMemo, useState } from 'react';
import { useDashboardConfig } from '../context/DashboardContext';

function formatDisplay(value, unit) {
  if (value === null || value === undefined) return '—';
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'dollars') return `$${Math.round(value).toLocaleString()}`;
  if (unit === 'index') return value.toFixed(2);
  return value.toLocaleString();
}

function formatCSV(value, unit) {
  if (value === null || value === undefined) return '';
  if (unit === 'percent') return value.toFixed(2);
  if (unit === 'dollars') return Math.round(value);
  if (unit === 'index') return value.toFixed(2);
  return value;
}

function getYearValue(trendArr, year) {
  if (!trendArr || !trendArr.length) return null;
  if (!year) return trendArr[0]?.value ?? null; // trend is desc, first = latest
  return trendArr.find(p => p.year === year)?.value ?? null;
}

function downloadCSV(filename, headers, rows) {
  const escape = v => {
    const s = v === null || v === undefined ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function TrendView({
  allData, indicators,
  selectedYear, comparisonCounty, comparisonData
}) {
  const { org } = useDashboardConfig();
  const [showAllYears, setShowAllYears] = useState(false);

  const parts = org.geography.label.split(',');
  const countyLabel = parts[0]?.trim() ?? org.geography.label;
  const stateLabel = parts[1] ? `${parts[1].trim()} Statewide` : 'Statewide';

  const allDataSafe = useMemo(() => allData || new Map(), [allData]);

  const indList = useMemo(() => indicators || [], [indicators]);

  // ── Build rows ─────────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    const result = [];

    if (!showAllYears) {
      // Single-year mode: State, County, Comparison, then all census tracts
      // Year column shows selectedYear or 'Latest' when no year is pinned
      const yearLabel = selectedYear ?? 'Latest';

      const stateRow = { geo: stateLabel, type: 'State', geoid: '', year: yearLabel };
      for (const ind of indList) {
        const d = allDataSafe.get(ind.id);
        stateRow[ind.id] = selectedYear
          ? getYearValue(d?.stateTrend, selectedYear)
          : (d?.stateValue ?? getYearValue(d?.stateTrend, null));
      }
      result.push(stateRow);

      const countyRow = { geo: countyLabel, type: 'County', geoid: '', year: yearLabel };
      for (const ind of indList) {
        const d = allDataSafe.get(ind.id);
        countyRow[ind.id] = selectedYear
          ? getYearValue(d?.trend, selectedYear)
          : (d?.countyValue ?? getYearValue(d?.trend, null));
      }
      result.push(countyRow);

      if (comparisonCounty && comparisonData) {
        const compRow = { geo: comparisonCounty.name, type: 'Comparison County', geoid: comparisonCounty.fips ?? '', year: yearLabel };
        for (const ind of indList) {
          const d = comparisonData.get(ind.id);
          compRow[ind.id] = selectedYear
            ? getYearValue(d?.trend, selectedYear)
            : (d?.countyValue ?? getYearValue(d?.trend, null));
        }
        result.push(compRow);
      }

      // Census tracts
      const tractMap = new Map();
      for (const ind of indList) {
        const d = allDataSafe.get(ind.id);
        const geos = selectedYear && d?.geographyByYear
          ? (d.geographyByYear.get(selectedYear) ?? d?.geography ?? [])
          : (d?.geography ?? []);
        for (const g of geos) {
          if (g.geoid?.length === 11 && !tractMap.has(g.geoid)) {
            tractMap.set(g.geoid, { geo: g.name || `Tract ${g.geoid.slice(-6)}`, type: 'Census Tract', geoid: g.geoid });
          }
        }
      }
      for (const [geoid, base] of tractMap.entries()) {
        const row = { ...base, year: yearLabel };
        for (const ind of indList) {
          const d = allDataSafe.get(ind.id);
          let val = null;
          if (selectedYear && d?.geographyByYear) {
            val = (d.geographyByYear.get(selectedYear) ?? []).find(g => g.geoid === geoid)?.value ?? null;
          }
          if (val === null) {
            val = (d?.geography ?? []).find(g => g.geoid === geoid)?.value ?? null;
          }
          row[ind.id] = val;
        }
        result.push(row);
      }

    } else {
      // All-years mode: State×year, County×year, Comparison×year, Tract×year
      const yearSet = new Set();
      for (const ind of indList) {
        const d = allDataSafe.get(ind.id);
        (d?.trend ?? []).forEach(p => yearSet.add(p.year));
        (d?.stateTrend ?? []).forEach(p => yearSet.add(p.year));
        if (comparisonCounty && comparisonData) {
          (comparisonData.get(ind.id)?.trend ?? []).forEach(p => yearSet.add(p.year));
        }
      }
      const years = [...yearSet].sort((a, b) => a - b);

      for (const year of years) {
        const row = { geo: stateLabel, type: 'State', geoid: '', year };
        for (const ind of indList) row[ind.id] = getYearValue(allDataSafe.get(ind.id)?.stateTrend, year);
        result.push(row);
      }

      for (const year of years) {
        const row = { geo: countyLabel, type: 'County', geoid: '', year };
        for (const ind of indList) row[ind.id] = getYearValue(allDataSafe.get(ind.id)?.trend, year);
        result.push(row);
      }

      if (comparisonCounty && comparisonData) {
        for (const year of years) {
          const row = { geo: comparisonCounty.name, type: 'Comparison County', geoid: comparisonCounty.fips ?? '', year };
          for (const ind of indList) row[ind.id] = getYearValue(comparisonData.get(ind.id)?.trend, year);
          result.push(row);
        }
      }

      // Tracts — collect geoids and their available years from geographyByYear
      const tractYearMap = new Map(); // geoid → { base, yearsWithData: Set }
      for (const ind of indList) {
        const d = allDataSafe.get(ind.id);
        if (!d?.geographyByYear) continue;
        for (const [yr, geos] of d.geographyByYear.entries()) {
          for (const g of geos) {
            if (g.geoid?.length !== 11) continue;
            if (!tractYearMap.has(g.geoid)) {
              tractYearMap.set(g.geoid, {
                base: { geo: g.name || `Tract ${g.geoid.slice(-6)}`, type: 'Census Tract', geoid: g.geoid },
                years: new Set()
              });
            }
            tractYearMap.get(g.geoid).years.add(yr);
          }
        }
      }

      for (const [geoid, { base, years: tractYears }] of tractYearMap.entries()) {
        const sortedTractYears = [...tractYears].sort((a, b) => a - b);
        for (const year of sortedTractYears) {
          const row = { ...base, year };
          for (const ind of indList) {
            const d = allDataSafe.get(ind.id);
            row[ind.id] = (d?.geographyByYear?.get(year) ?? []).find(g => g.geoid === geoid)?.value ?? null;
          }
          result.push(row);
        }
      }
    }

    return result;
  }, [allDataSafe, indList, selectedYear, showAllYears, comparisonCounty, comparisonData, countyLabel, stateLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleDownload() {
    const headers = ['Geography', 'Type', 'GEOID', 'Year', ...indList.map(i => i.label)];
    const csvRows = rows.map(row => [
      row.geo,
      row.type,
      row.geoid ?? '',
      row.year,
      ...indList.map(ind => formatCSV(row[ind.id], ind.unit))
    ]);
    const year = selectedYear ? `-${selectedYear}` : '';
    downloadCSV(`community-data${year}.csv`, headers, csvRows);
  }

  const yearLabel = selectedYear ? `${selectedYear}` : 'Latest Available';

  return (
    <div className="data-table-view">
      <div className="data-table-toolbar">
        <div className="dtv-toggle-group">
          <button
            className={`dtv-toggle-btn${!showAllYears ? ' dtv-toggle-btn--active' : ''}`}
            onClick={() => setShowAllYears(false)}
          >
            {yearLabel}
          </button>
          <button
            className={`dtv-toggle-btn${showAllYears ? ' dtv-toggle-btn--active' : ''}`}
            onClick={() => setShowAllYears(true)}
          >
            All Years
          </button>
        </div>
        <button className="dtv-download-btn" onClick={handleDownload}>
          ↓ Download CSV
        </button>
      </div>

      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th className="dt-th-geo">Geography</th>
              <th className="dt-th-type">Type</th>
              <th className="dt-th-year">Year</th>
              {indList.map(ind => (
                <th key={ind.id} className="dt-th-value">{ind.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className={
                  row.type === 'Census Tract' ? 'dt-row--tract' :
                  row.type === 'Comparison County' ? 'dt-row--comparison' :
                  row.type === 'State' ? 'dt-row--state' : ''
                }
              >
                <td className="dt-geo-name">{row.geo}</td>
                <td className="dt-geo-type">{row.type}</td>
                <td className="dt-year">{row.year}</td>
                {indList.map(ind => (
                  <td key={ind.id} className="dt-value">
                    {formatDisplay(row[ind.id], ind.unit)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
